import { useState, useCallback, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery } from '@tanstack/react-query';
import {
  GripVertical,
  Copy,
  Trash2,
  Type,
  Image as ImageIcon,
  MousePointerClick,
  Minus,
  ArrowUpDown,
  Columns,
  LayoutTemplate,
  Eye,
  EyeOff,
  Bold,
  Italic,
  Link,
  AlignLeft,
  AlignCenter,
  AlignRight,
  X,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { api } from '../../lib/api';
import { MergeTagPicker } from './MergeTagPicker';
import { EmailPreview } from './EmailPreview';
import {
  emailBlocksToHtml,
  emailBlocksToText,
  generateBlockId,
  createDefaultBlock,
} from './email-utils';
import type {
  EmailBlock,
  EmailBlockData,
  EmailBlockType,
  TextBlockData,
  ImageBlockData,
  ButtonBlockData,
  DividerBlockData,
  SpacerBlockData,
  HeaderBlockData,
  ColumnsBlockData,
} from './types';

// -- Media item interface (from CommunicationsPage) --
interface MediaItem {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  altText?: string;
}

// -- Block palette items --
interface PaletteItem {
  type: EmailBlockType;
  label: string;
  icon: typeof Type;
}

const PALETTE_ITEMS: PaletteItem[] = [
  { type: 'header', label: 'Header', icon: LayoutTemplate },
  { type: 'text', label: 'Text', icon: Type },
  { type: 'image', label: 'Image', icon: ImageIcon },
  { type: 'button', label: 'Button', icon: MousePointerClick },
  { type: 'divider', label: 'Divider', icon: Minus },
  { type: 'spacer', label: 'Spacer', icon: ArrowUpDown },
  { type: 'columns', label: 'Columns', icon: Columns },
];

// ============================================
// Props
// ============================================
interface EmailEditorProps {
  value: string;
  onChange: (value: { blocksJson: string; html: string; text: string }) => void;
  organizationName?: string;
}

// ============================================
// Attempt to parse saved blocks JSON
// ============================================
function parseBlocksJson(value: string): EmailBlock[] | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].id && parsed[0].data) {
      return parsed as EmailBlock[];
    }
  } catch {
    // Not valid JSON
  }
  return null;
}

// ============================================
// Main Editor
// ============================================
export function EmailEditor({ value, onChange, organizationName = 'Organization' }: EmailEditorProps) {
  const [blocks, setBlocks] = useState<EmailBlock[]>(() => {
    const parsed = parseBlocksJson(value);
    if (parsed) return parsed;
    // For empty content, start with a default header + text block
    if (!value) {
      return [
        { id: generateBlockId(), data: createDefaultBlock('header', organizationName) },
        { id: generateBlockId(), data: createDefaultBlock('text') },
      ];
    }
    // For legacy HTML, return empty and show fallback
    return [];
  });
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isFallbackMode] = useState(() => {
    // If the value is existing HTML (not blocks JSON) and not empty, use fallback
    return !!value && !parseBlocksJson(value);
  });
  const [fallbackHtml, setFallbackHtml] = useState(isFallbackMode ? value : '');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId) || null;

  const emitChange = useCallback(
    (updatedBlocks: EmailBlock[]) => {
      const html = emailBlocksToHtml(updatedBlocks, organizationName);
      const text = emailBlocksToText(updatedBlocks);
      const blocksJson = JSON.stringify(updatedBlocks);
      onChange({ blocksJson, html, text });
    },
    [onChange, organizationName]
  );

  const updateBlocks = useCallback(
    (updatedBlocks: EmailBlock[]) => {
      setBlocks(updatedBlocks);
      emitChange(updatedBlocks);
    },
    [emitChange]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = blocks.findIndex((b) => b.id === active.id);
        const newIndex = blocks.findIndex((b) => b.id === over.id);
        const newBlocks = arrayMove(blocks, oldIndex, newIndex);
        updateBlocks(newBlocks);
      }
    },
    [blocks, updateBlocks]
  );

  const addBlock = useCallback(
    (type: EmailBlockType) => {
      const newBlock: EmailBlock = {
        id: generateBlockId(),
        data: createDefaultBlock(type, organizationName),
      };
      const newBlocks = [...blocks, newBlock];
      updateBlocks(newBlocks);
      setSelectedBlockId(newBlock.id);
    },
    [blocks, updateBlocks, organizationName]
  );

  const duplicateBlock = useCallback(
    (id: string) => {
      const blockIndex = blocks.findIndex((b) => b.id === id);
      if (blockIndex === -1) return;
      const original = blocks[blockIndex];
      const newBlock: EmailBlock = {
        id: generateBlockId(),
        data: { ...original.data },
      };
      const newBlocks = [...blocks];
      newBlocks.splice(blockIndex + 1, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedBlockId(newBlock.id);
    },
    [blocks, updateBlocks]
  );

  const deleteBlock = useCallback(
    (id: string) => {
      const newBlocks = blocks.filter((b) => b.id !== id);
      updateBlocks(newBlocks);
      if (selectedBlockId === id) {
        setSelectedBlockId(null);
      }
    },
    [blocks, updateBlocks, selectedBlockId]
  );

  const updateBlockData = useCallback(
    (id: string, data: EmailBlockData) => {
      const newBlocks = blocks.map((b) => (b.id === id ? { ...b, data } : b));
      updateBlocks(newBlocks);
    },
    [blocks, updateBlocks]
  );

  const handleMergeTagInsert = useCallback(
    (tag: string) => {
      if (!selectedBlock || selectedBlock.data.type !== 'text') return;
      const textData = selectedBlock.data as TextBlockData;
      updateBlockData(selectedBlock.id, {
        ...textData,
        content: textData.content + tag,
      });
    },
    [selectedBlock, updateBlockData]
  );

  // Fallback mode for legacy HTML content
  if (isFallbackMode) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <span className="text-sm text-amber-800">
            This content was created with the HTML editor. You can edit the HTML below, or clear it to use the visual editor.
          </span>
          <button
            type="button"
            onClick={() => {
              // eslint-disable-next-line no-alert
              if (window.confirm('Clear existing HTML and switch to the visual editor? This cannot be undone.')) {
                window.location.reload();
              }
            }}
            className="shrink-0 text-xs font-medium text-amber-700 underline hover:text-amber-900"
          >
            Switch to Visual Editor
          </button>
        </div>
        <textarea
          value={fallbackHtml}
          onChange={(e) => {
            setFallbackHtml(e.target.value);
            onChange({ blocksJson: '', html: e.target.value, text: '' });
          }}
          rows={16}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 font-mono text-sm"
        />
      </div>
    );
  }

  // Preview mode
  if (showPreview) {
    return (
      <div className="border border-gray-200 rounded-xl overflow-hidden" style={{ height: '700px' }}>
        <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
          <span className="text-sm font-medium text-gray-700">Email Preview</span>
          <button
            type="button"
            onClick={() => setShowPreview(false)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            <EyeOff className="h-3.5 w-3.5" />
            Close Preview
          </button>
        </div>
        <EmailPreview html={emailBlocksToHtml(blocks, organizationName)} />
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 bg-gray-50 flex-wrap">
        <MergeTagPicker onInsert={handleMergeTagInsert} />
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setShowPreview(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Eye className="h-3.5 w-3.5" />
          Preview
        </button>
      </div>

      <div className="flex" style={{ minHeight: '550px' }}>
        {/* Left sidebar: Block palette */}
        <div className="w-48 shrink-0 border-r border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Add Block</p>
          <div className="space-y-1.5">
            {PALETTE_ITEMS.map((item) => (
              <button
                key={item.type}
                type="button"
                onClick={() => addBlock(item.type)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 transition-colors"
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Center: Canvas */}
        <div
          className="flex-1 overflow-auto bg-gray-100 p-6"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedBlockId(null);
          }}
        >
          <div className="mx-auto" style={{ maxWidth: '600px' }}>
            {blocks.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <LayoutTemplate className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm font-medium">No blocks yet</p>
                <p className="text-xs mt-1">Click a block type on the left to get started</p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={blocks.map((b) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-1">
                    {blocks.map((block) => (
                      <SortableBlock
                        key={block.id}
                        block={block}
                        isSelected={block.id === selectedBlockId}
                        onSelect={() => setSelectedBlockId(block.id)}
                        onDuplicate={() => duplicateBlock(block.id)}
                        onDelete={() => deleteBlock(block.id)}
                        onUpdateData={(data) => updateBlockData(block.id, data)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>

        {/* Right sidebar: Settings */}
        <div className="w-72 shrink-0 border-l border-gray-200 bg-white overflow-y-auto">
          {selectedBlock ? (
            <BlockSettings
              block={selectedBlock}
              onUpdate={(data) => updateBlockData(selectedBlock.id, data)}
              onClose={() => setSelectedBlockId(null)}
            />
          ) : (
            <div className="p-4 text-center text-gray-400 mt-12">
              <p className="text-sm">Select a block to edit its settings</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Sortable Block wrapper
// ============================================
interface SortableBlockProps {
  block: EmailBlock;
  isSelected: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onUpdateData: (data: EmailBlockData) => void;
}

function SortableBlock({
  block,
  isSelected,
  onSelect,
  onDuplicate,
  onDelete,
  onUpdateData,
}: SortableBlockProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative bg-white rounded-lg border-2 transition-colors',
        isSelected ? 'border-primary-400 shadow-sm' : 'border-transparent hover:border-gray-300'
      )}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      {/* Block controls */}
      <div
        className={cn(
          'absolute -left-1 top-1/2 -translate-y-1/2 -translate-x-full flex flex-col gap-0.5 transition-opacity',
          isSelected || isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        )}
      >
        <button
          type="button"
          className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </div>
      <div
        className={cn(
          'absolute -right-1 top-1 translate-x-full flex flex-col gap-0.5 transition-opacity',
          isSelected || isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        )}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          className="p-1 text-gray-400 hover:text-primary-600 bg-white border border-gray-200 rounded"
          title="Duplicate"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="p-1 text-gray-400 hover:text-red-600 bg-white border border-gray-200 rounded"
          title="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Block content renderer */}
      <BlockRenderer block={block} onUpdateData={onUpdateData} />
    </div>
  );
}

// ============================================
// Block Renderer (canvas visual)
// ============================================
function BlockRenderer({
  block,
  onUpdateData,
}: {
  block: EmailBlock;
  onUpdateData: (data: EmailBlockData) => void;
}) {
  const { data } = block;

  switch (data.type) {
    case 'header':
      return (
        <div
          style={{ background: data.backgroundColor }}
          className="rounded-t-md px-5 py-8 text-center"
        >
          <h1 style={{ color: data.textColor }} className="text-xl font-bold">
            {data.organizationName}
          </h1>
        </div>
      );

    case 'text':
      return (
        <div className="px-6 py-4">
          <InlineTextEditor
            content={data.content}
            alignment={data.alignment}
            onChange={(content) => onUpdateData({ ...data, content })}
          />
        </div>
      );

    case 'image':
      return (
        <div className={cn('px-6 py-4', `text-${data.alignment}`)}>
          {data.url ? (
            <img
              src={data.url}
              alt={data.alt}
              className={cn(
                'max-w-full h-auto rounded',
                data.width === 'medium' && 'max-w-[400px]',
                data.width === 'small' && 'max-w-[200px]',
                data.alignment === 'center' && 'mx-auto',
                data.alignment === 'right' && 'ml-auto'
              )}
            />
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg py-8 text-center text-gray-400">
              <ImageIcon className="h-8 w-8 mx-auto mb-2" />
              <p className="text-sm">Select an image in the settings panel</p>
            </div>
          )}
        </div>
      );

    case 'button':
      return (
        <div className={cn('px-6 py-4', `text-${data.alignment}`)}>
          <span
            className="inline-block px-7 py-3 font-semibold text-sm"
            style={{
              backgroundColor: data.backgroundColor,
              color: data.textColor,
              borderRadius: `${data.borderRadius}px`,
            }}
          >
            {data.text}
          </span>
        </div>
      );

    case 'divider':
      return (
        <div className="px-6 py-2">
          <hr
            style={{
              borderStyle: data.style,
              borderColor: data.color,
              borderWidth: `${data.thickness}px 0 0 0`,
            }}
          />
        </div>
      );

    case 'spacer':
      return (
        <div
          className="flex items-center justify-center text-gray-300"
          style={{ height: `${data.height}px` }}
        >
          <ArrowUpDown className="h-4 w-4" />
          <span className="text-xs ml-1">{data.height}px</span>
        </div>
      );

    case 'columns':
      return (
        <div className="px-6 py-4 grid grid-cols-2 gap-4">
          <div className="border border-dashed border-gray-200 rounded p-3 min-h-[60px]">
            {data.left.type === 'image' && data.left.url ? (
              <img src={data.left.url} alt={data.left.alt || ''} className="max-w-full h-auto" />
            ) : (
              <div
                className="text-sm text-gray-700"
                dangerouslySetInnerHTML={{ __html: data.left.content }}
              />
            )}
          </div>
          <div className="border border-dashed border-gray-200 rounded p-3 min-h-[60px]">
            {data.right.type === 'image' && data.right.url ? (
              <img src={data.right.url} alt={data.right.alt || ''} className="max-w-full h-auto" />
            ) : (
              <div
                className="text-sm text-gray-700"
                dangerouslySetInnerHTML={{ __html: data.right.content }}
              />
            )}
          </div>
        </div>
      );
  }
}

// ============================================
// Inline text editor with contentEditable
// ============================================
function InlineTextEditor({
  content,
  alignment,
  onChange,
}: {
  content: string;
  alignment: string;
  onChange: (content: string) => void;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastContentRef = useRef(content);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      if (html !== lastContentRef.current) {
        lastContentRef.current = html;
        onChange(html);
      }
    }
  }, [onChange]);

  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  return (
    <div>
      <div className="flex items-center gap-0.5 mb-2 -mt-1">
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('bold'); }}
          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          title="Bold"
        >
          <Bold className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('italic'); }}
          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          title="Italic"
        >
          <Italic className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            // eslint-disable-next-line no-alert
            const url = prompt('Enter URL:');
            if (url) execCommand('createLink', url);
          }}
          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          title="Insert Link"
        >
          <Link className="h-3.5 w-3.5" />
        </button>
        <div className="w-px h-4 bg-gray-200 mx-1" />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('justifyLeft'); }}
          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          title="Align Left"
        >
          <AlignLeft className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('justifyCenter'); }}
          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          title="Align Center"
        >
          <AlignCenter className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault(); execCommand('justifyRight'); }}
          className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
          title="Align Right"
        >
          <AlignRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        dangerouslySetInnerHTML={{ __html: content }}
        style={{ textAlign: alignment as 'left' | 'center' | 'right' }}
        className="min-h-[40px] text-sm text-gray-800 leading-relaxed focus:outline-none [&_a]:text-primary-600 [&_a]:underline"
      />
    </div>
  );
}

// ============================================
// Block Settings Panel
// ============================================
function BlockSettings({
  block,
  onUpdate,
  onClose,
}: {
  block: EmailBlock;
  onUpdate: (data: EmailBlockData) => void;
  onClose: () => void;
}) {
  const { data } = block;

  const blockTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      header: 'Header',
      text: 'Text',
      image: 'Image',
      button: 'Button',
      divider: 'Divider',
      spacer: 'Spacer',
      columns: 'Columns',
    };
    return labels[type] || type;
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">{blockTypeLabel(data.type)} Settings</h3>
        <button
          type="button"
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {data.type === 'header' && (
        <HeaderSettings data={data} onUpdate={onUpdate} />
      )}
      {data.type === 'text' && (
        <TextSettings data={data} onUpdate={onUpdate} />
      )}
      {data.type === 'image' && (
        <ImageSettings data={data} onUpdate={onUpdate} />
      )}
      {data.type === 'button' && (
        <ButtonSettings data={data} onUpdate={onUpdate} />
      )}
      {data.type === 'divider' && (
        <DividerSettings data={data} onUpdate={onUpdate} />
      )}
      {data.type === 'spacer' && (
        <SpacerSettings data={data} onUpdate={onUpdate} />
      )}
      {data.type === 'columns' && (
        <ColumnsSettings data={data} onUpdate={onUpdate} />
      )}
    </div>
  );
}

// -- Setting field helpers --
function SettingLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-xs font-medium text-gray-600 mb-1">{children}</label>;
}

function SettingInput({
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  value: string | number;
  onChange: (val: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
    />
  );
}

function SettingColor({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (val: string) => void;
  label: string;
}) {
  return (
    <div>
      <SettingLabel>{label}</SettingLabel>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value.startsWith('#') ? value : '#1e40af'}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 rounded border border-gray-300 cursor-pointer"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm font-mono focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>
    </div>
  );
}

function AlignmentPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (val: 'left' | 'center' | 'right') => void;
}) {
  return (
    <div>
      <SettingLabel>Alignment</SettingLabel>
      <div className="flex gap-1">
        {(['left', 'center', 'right'] as const).map((align) => {
          const Icon = align === 'left' ? AlignLeft : align === 'center' ? AlignCenter : AlignRight;
          return (
            <button
              key={align}
              type="button"
              onClick={() => onChange(align)}
              className={cn(
                'flex-1 flex items-center justify-center py-1.5 border rounded-md text-xs',
                value === align
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              )}
            >
              <Icon className="h-3.5 w-3.5" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// -- Per-block settings --

function HeaderSettings({
  data,
  onUpdate,
}: {
  data: HeaderBlockData;
  onUpdate: (data: EmailBlockData) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <SettingLabel>Organization Name</SettingLabel>
        <SettingInput
          value={data.organizationName}
          onChange={(val) => onUpdate({ ...data, organizationName: val })}
        />
      </div>
      <SettingColor
        label="Background Color"
        value={data.backgroundColor}
        onChange={(val) => onUpdate({ ...data, backgroundColor: val })}
      />
      <SettingColor
        label="Text Color"
        value={data.textColor}
        onChange={(val) => onUpdate({ ...data, textColor: val })}
      />
    </div>
  );
}

function TextSettings({
  data,
  onUpdate,
}: {
  data: TextBlockData;
  onUpdate: (data: EmailBlockData) => void;
}) {
  return (
    <div className="space-y-3">
      <AlignmentPicker
        value={data.alignment}
        onChange={(val) => onUpdate({ ...data, alignment: val })}
      />
      <p className="text-xs text-gray-500">
        Edit text directly in the canvas. Use the formatting toolbar above the text block.
      </p>
    </div>
  );
}

function ImageSettings({
  data,
  onUpdate,
}: {
  data: ImageBlockData;
  onUpdate: (data: EmailBlockData) => void;
}) {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <div className="space-y-3">
      <div>
        <SettingLabel>Image</SettingLabel>
        {data.url ? (
          <div className="space-y-2">
            <img
              src={data.url}
              alt={data.alt}
              className="w-full h-auto rounded border border-gray-200"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                className="flex-1 text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                Change
              </button>
              <button
                type="button"
                onClick={() => onUpdate({ ...data, url: '', alt: '' })}
                className="flex-1 text-xs font-medium text-red-500 hover:text-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            className="w-full flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100"
          >
            <ImageIcon className="h-4 w-4" />
            Select from Media Library
          </button>
        )}
      </div>

      <div>
        <SettingLabel>Image URL (external)</SettingLabel>
        <SettingInput
          value={data.url}
          onChange={(val) => onUpdate({ ...data, url: val })}
          placeholder="https://example.com/image.jpg"
        />
      </div>

      <div>
        <SettingLabel>Alt Text</SettingLabel>
        <SettingInput
          value={data.alt}
          onChange={(val) => onUpdate({ ...data, alt: val })}
          placeholder="Image description"
        />
      </div>

      <div>
        <SettingLabel>Size</SettingLabel>
        <select
          value={data.width}
          onChange={(e) => onUpdate({ ...data, width: e.target.value as 'full' | 'medium' | 'small' })}
          className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="full">Full Width</option>
          <option value="medium">Medium (400px)</option>
          <option value="small">Small (200px)</option>
        </select>
      </div>

      <AlignmentPicker
        value={data.alignment}
        onChange={(val) => onUpdate({ ...data, alignment: val })}
      />

      <div>
        <SettingLabel>Link URL (optional)</SettingLabel>
        <SettingInput
          value={data.linkUrl}
          onChange={(val) => onUpdate({ ...data, linkUrl: val })}
          placeholder="https://..."
        />
      </div>

      {showPicker && (
        <MediaLibraryPicker
          onSelect={(url, alt) => {
            onUpdate({ ...data, url, alt: alt || data.alt });
            setShowPicker(false);
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}

function ButtonSettings({
  data,
  onUpdate,
}: {
  data: ButtonBlockData;
  onUpdate: (data: EmailBlockData) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <SettingLabel>Button Text</SettingLabel>
        <SettingInput
          value={data.text}
          onChange={(val) => onUpdate({ ...data, text: val })}
          placeholder="Click Here"
        />
      </div>
      <div>
        <SettingLabel>Link URL</SettingLabel>
        <SettingInput
          value={data.url}
          onChange={(val) => onUpdate({ ...data, url: val })}
          placeholder="https://..."
        />
      </div>
      <SettingColor
        label="Button Color"
        value={data.backgroundColor}
        onChange={(val) => onUpdate({ ...data, backgroundColor: val })}
      />
      <SettingColor
        label="Text Color"
        value={data.textColor}
        onChange={(val) => onUpdate({ ...data, textColor: val })}
      />
      <AlignmentPicker
        value={data.alignment}
        onChange={(val) => onUpdate({ ...data, alignment: val })}
      />
      <div>
        <SettingLabel>Border Radius</SettingLabel>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min="0"
            max="24"
            value={data.borderRadius}
            onChange={(e) => onUpdate({ ...data, borderRadius: parseInt(e.target.value) })}
            className="flex-1"
          />
          <span className="text-xs text-gray-500 w-8 text-right">{data.borderRadius}px</span>
        </div>
      </div>
    </div>
  );
}

function DividerSettings({
  data,
  onUpdate,
}: {
  data: DividerBlockData;
  onUpdate: (data: EmailBlockData) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <SettingLabel>Style</SettingLabel>
        <select
          value={data.style}
          onChange={(e) => onUpdate({ ...data, style: e.target.value as 'solid' | 'dashed' | 'dotted' })}
          className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="solid">Solid</option>
          <option value="dashed">Dashed</option>
          <option value="dotted">Dotted</option>
        </select>
      </div>
      <SettingColor
        label="Color"
        value={data.color}
        onChange={(val) => onUpdate({ ...data, color: val })}
      />
      <div>
        <SettingLabel>Thickness</SettingLabel>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min="1"
            max="5"
            value={data.thickness}
            onChange={(e) => onUpdate({ ...data, thickness: parseInt(e.target.value) })}
            className="flex-1"
          />
          <span className="text-xs text-gray-500 w-8 text-right">{data.thickness}px</span>
        </div>
      </div>
    </div>
  );
}

function SpacerSettings({
  data,
  onUpdate,
}: {
  data: SpacerBlockData;
  onUpdate: (data: EmailBlockData) => void;
}) {
  return (
    <div className="space-y-3">
      <div>
        <SettingLabel>Height</SettingLabel>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min="8"
            max="120"
            step="4"
            value={data.height}
            onChange={(e) => onUpdate({ ...data, height: parseInt(e.target.value) })}
            className="flex-1"
          />
          <span className="text-xs text-gray-500 w-10 text-right">{data.height}px</span>
        </div>
      </div>
    </div>
  );
}

function ColumnsSettings({
  data,
  onUpdate,
}: {
  data: ColumnsBlockData;
  onUpdate: (data: EmailBlockData) => void;
}) {
  const [showLeftImagePicker, setShowLeftImagePicker] = useState(false);
  const [showRightImagePicker, setShowRightImagePicker] = useState(false);

  return (
    <div className="space-y-4">
      {/* Left Column */}
      <div>
        <p className="text-xs font-semibold text-gray-700 mb-2">Left Column</p>
        <div className="space-y-2">
          <select
            value={data.left.type}
            onChange={(e) =>
              onUpdate({
                ...data,
                left: { ...data.left, type: e.target.value as 'text' | 'image' },
              })
            }
            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="text">Text</option>
            <option value="image">Image</option>
          </select>
          {data.left.type === 'text' ? (
            <textarea
              value={data.left.content}
              onChange={(e) =>
                onUpdate({ ...data, left: { ...data.left, content: e.target.value } })
              }
              rows={3}
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Left column text (HTML)"
            />
          ) : (
            <div className="space-y-2">
              <SettingInput
                value={data.left.url || ''}
                onChange={(val) => onUpdate({ ...data, left: { ...data.left, url: val } })}
                placeholder="Image URL"
              />
              <button
                type="button"
                onClick={() => setShowLeftImagePicker(true)}
                className="w-full text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1.5 rounded hover:bg-primary-100"
              >
                Select from Media Library
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right Column */}
      <div>
        <p className="text-xs font-semibold text-gray-700 mb-2">Right Column</p>
        <div className="space-y-2">
          <select
            value={data.right.type}
            onChange={(e) =>
              onUpdate({
                ...data,
                right: { ...data.right, type: e.target.value as 'text' | 'image' },
              })
            }
            className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="text">Text</option>
            <option value="image">Image</option>
          </select>
          {data.right.type === 'text' ? (
            <textarea
              value={data.right.content}
              onChange={(e) =>
                onUpdate({ ...data, right: { ...data.right, content: e.target.value } })
              }
              rows={3}
              className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="Right column text (HTML)"
            />
          ) : (
            <div className="space-y-2">
              <SettingInput
                value={data.right.url || ''}
                onChange={(val) => onUpdate({ ...data, right: { ...data.right, url: val } })}
                placeholder="Image URL"
              />
              <button
                type="button"
                onClick={() => setShowRightImagePicker(true)}
                className="w-full text-xs font-medium text-primary-600 bg-primary-50 px-3 py-1.5 rounded hover:bg-primary-100"
              >
                Select from Media Library
              </button>
            </div>
          )}
        </div>
      </div>

      {showLeftImagePicker && (
        <MediaLibraryPicker
          onSelect={(url, alt) => {
            onUpdate({ ...data, left: { ...data.left, url, alt: alt || '' } });
            setShowLeftImagePicker(false);
          }}
          onClose={() => setShowLeftImagePicker(false)}
        />
      )}
      {showRightImagePicker && (
        <MediaLibraryPicker
          onSelect={(url, alt) => {
            onUpdate({ ...data, right: { ...data.right, url, alt: alt || '' } });
            setShowRightImagePicker(false);
          }}
          onClose={() => setShowRightImagePicker(false)}
        />
      )}
    </div>
  );
}

// ============================================
// Media Library Picker (reused from WebsitePage pattern)
// ============================================
function MediaLibraryPicker({
  onSelect,
  onClose,
}: {
  onSelect: (url: string, alt: string) => void;
  onClose: () => void;
}) {
  const { data: mediaItems, isLoading } = useQuery({
    queryKey: ['media-images-for-email-editor'],
    queryFn: async () => {
      const response = await api.get<{ data: MediaItem[] }>('/media?mimeType=image&limit=100');
      if (!response.success) throw new Error(response.error?.message);
      return response.data?.data || [];
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-gray-900">Select Image</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
          ) : !mediaItems || mediaItems.length === 0 ? (
            <div className="text-center py-12">
              <ImageIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No images uploaded yet.</p>
              <p className="text-sm text-gray-400 mt-1">Go to Media Library to upload images first.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
              {mediaItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelect(item.url, item.altText || item.originalName)}
                  className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-primary-500 focus:border-primary-500 focus:outline-none transition-colors group relative"
                >
                  <img
                    src={item.url}
                    alt={item.altText || item.originalName}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                      Select
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
